import React, { useState } from "react";
import Card from "./Card";
import Button from "./Button";
import API from "../api/api";

const REPORTES = [
  {
    key: "cuentas-por-cobrar",
    label: "Cuentas por Cobrar Cedidas",
    description: "Facturas vendidas o cedidas dentro del flujo de factoring.",
  },
  {
    key: "intereses",
    label: "Intereses y Comisiones",
    description: "Rentabilidad por operación: interés, comisión y ganancia total.",
  },
  {
    key: "flujo-caja",
    label: "Flujo de Caja",
    description: "Entradas y salidas: desembolsos, pagos y comisiones.",
  },
  {
    key: "estados",
    label: "Facturas por Estado",
    description: "Resumen agregado de publicadas, negociadas, vendidas y vencidas.",
  },
  {
    key: "morosidad",
    label: "Morosidad",
    description: "Facturas vencidas no cobradas y clasificación de riesgo.",
  },
  {
    key: "vencimientos",
    label: "Vencimientos",
    description: "Facturas próximas a vencer dentro del rango seleccionado.",
  },
];

const LABELS = {
  factura_id: "Factura ID",
  vendedor: "Vendedor",
  comprador: "Comprador",
  deudor: "Deudor",
  monto_nominal: "Monto nominal",
  monto_financiado: "Monto financiado",
  fecha_vencimiento: "Vencimiento",
  estado: "Estado",
  monto: "Monto",
  tasa_interes: "Tasa/descuento",
  interes_generado: "Interés generado",
  comision: "Comisión",
  ganancia_total: "Ganancia total",
  fecha: "Fecha",
  tipo: "Tipo",
  concepto: "Concepto",
  dias_atraso: "Días atraso",
  riesgo: "Riesgo",
  cdc: "CDC",
  dias_restantes: "Días restantes",
  indicador: "Indicador",
  valor: "Valor",
};

function formatValue(key, value) {
  if (value === null || value === undefined || value === "") return "-";

  if (
    key.includes("monto") ||
    key === "comision" ||
    key === "interes_generado" ||
    key === "ganancia_total"
  ) {
    return `Gs ${Number(value).toLocaleString("es-PY")}`;
  }

  if (key === "tasa_interes") {
    return `${(Number(value) * 100).toFixed(2)}%`;
  }

  if (key.includes("fecha")) {
    return new Date(value).toLocaleDateString("es-PY");
  }

  return String(value);
}

function normalizeData(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    return Object.entries(data)
      .filter(([, value]) => !Array.isArray(value) && typeof value !== "object")
      .map(([key, value]) => ({
        indicador: key,
        valor: value,
      }));
  }

  return [];
}

function getColumns(rows) {
  if (!rows.length) return [];
  const keys = new Set();
  rows.forEach((row) => {
    Object.keys(row).forEach((key) => keys.add(key));
  });
  return Array.from(keys);
}

function buildQuery(filters, exportFormat) {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });

  if (exportFormat) params.append("export", exportFormat);

  const query = params.toString();
  return query ? `?${query}` : "";
}

export default function ReportesContables() {
  const [reporte, setReporte] = useState("cuentas-por-cobrar");
  const [filters, setFilters] = useState({
    desde: "",
    hasta: "",
    cliente: "",
    estado: "",
  });
  const [rows, setRows] = useState([]);
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reporteSeleccionado = REPORTES.find((item) => item.key === reporte);
  const columns = getColumns(rows);

  const cargarReporte = async () => {
    try {
      setLoading(true);
      setError("");

      const res = await API.get(`/reportes/${reporte}/${buildQuery(filters)}`);
      setRawData(res.data);
      setRows(normalizeData(res.data));
    } catch (err) {
      console.error("Error cargando reporte:", err);
      setError(
        err.response?.data?.detail ||
          err.response?.data?.error ||
          "No se pudo cargar el reporte contable."
      );
      setRows([]);
      setRawData(null);
    } finally {
      setLoading(false);
    }
  };

  const exportarReporte = async (format) => {
    try {
      setError("");
      const res = await API.get(
        `/reportes/${reporte}/${buildQuery(filters, format)}`,
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${reporte}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exportando reporte:", err);
      setError(
        err.response?.data?.detail ||
          "No se pudo exportar el reporte. Para XLSX, confirme que openpyxl esté instalado en backend."
      );
    }
  };

  return (
    <Card className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-primary">
          Reportes Contables
        </h2>
        <p className="text-sm text-slate-600">
          Reportes basados en el flujo real: factura, publicación, oferta, cesión, cobro y cierre.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-medium">Reporte</label>
          <select
            className="border rounded p-2 w-full"
            value={reporte}
            onChange={(e) => {
              setReporte(e.target.value);
              setRows([]);
              setRawData(null);
            }}
          >
            {REPORTES.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-1">
            {reporteSeleccionado?.description}
          </p>
        </div>

        <div>
          <label className="text-sm font-medium">Estado operación</label>
          <select
            className="border rounded p-2 w-full"
            value={filters.estado}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, estado: e.target.value }))
            }
          >
            <option value="">Todos</option>
            <option value="PENDIENTE">Pendiente</option>
            <option value="PAGADO">Pagado/Cerrado</option>
            <option value="FALLIDA">Fallida</option>
          </select>
        </div>

        <div>
          <label className="text-sm font-medium">Desde</label>
          <input
            type="date"
            className="border rounded p-2 w-full"
            value={filters.desde}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, desde: e.target.value }))
            }
          />
        </div>

        <div>
          <label className="text-sm font-medium">Hasta</label>
          <input
            type="date"
            className="border rounded p-2 w-full"
            value={filters.hasta}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, hasta: e.target.value }))
            }
          />
        </div>

        <div>
          <label className="text-sm font-medium">Cliente vendedor ID</label>
          <input
            type="number"
            className="border rounded p-2 w-full"
            placeholder="Opcional"
            value={filters.cliente}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, cliente: e.target.value }))
            }
          />
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-2">
        <Button disabled={loading} onClick={cargarReporte}>
          {loading ? "Cargando..." : "Generar reporte"}
        </Button>
        <Button
          className="bg-slate-700 hover:bg-slate-800"
          disabled={!rawData}
          onClick={() => exportarReporte("csv")}
        >
          Exportar CSV
        </Button>
        <Button
          className="bg-slate-700 hover:bg-slate-800"
          disabled={!rawData}
          onClick={() => exportarReporte("xlsx")}
        >
          Exportar XLSX
        </Button>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 text-red-700 rounded p-3 text-sm">
          {error}
        </div>
      )}

      {!rows.length ? (
        <p className="text-sm text-slate-500">
          Genere un reporte para visualizar resultados.
        </p>
      ) : (
        <div className="overflow-auto border rounded">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-sand/40 text-left">
                {columns.map((column) => (
                  <th key={column} className="py-2 px-3 whitespace-nowrap">
                    {LABELS[column] || column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index} className="border-b align-top">
                  {columns.map((column) => (
                    <td key={column} className="py-2 px-3 whitespace-nowrap">
                      {formatValue(column, row[column])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
